// PagedResult is used in other files that import from this file

/**
 * Schedule type values.
 *
 * Note: `erasableSyntaxOnly` is enabled in `tsconfig.app.json`, so we avoid `enum`
 * (enums emit runtime code). Use `as const` + union types instead.
 */
export const ScheduleTypes = {
  Cron: 'Cron',
  Interval: 'Interval',
  OneTime: 'OneTime',
} as const;

export type ScheduleType = (typeof ScheduleTypes)[keyof typeof ScheduleTypes];

/**
 * Execution status values.
 */
export const ExecutionStatuses = {
  Pending: 'Pending',
  Running: 'Running',
  Completed: 'Completed',
  Failed: 'Failed',
  Cancelled: 'Cancelled',
} as const;

export type ExecutionStatus = (typeof ExecutionStatuses)[keyof typeof ExecutionStatuses];

/**
 * Schedule data transfer object.
 */
export interface ScheduleDto {
  id: string;
  name: string;
  description?: string;
  agentDefinitionId: string;
  agentName: string;
  scheduleType: ScheduleType;
  cronExpression?: string;
  recurrencePattern?: string;
  scheduledDateTime?: string;
  interval?: string; // ISO 8601 duration format
  timeZone: string;
  isActive: boolean;
  nextExecutionAt?: string;
  lastExecutionAt?: string;
  parametersJson?: string;
  maxRetries?: number;
  retryDelay?: string; // ISO 8601 duration format
  tenantId: string;
  projectId?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Schedule execution data transfer object.
 */
export interface ScheduleExecutionDto {
  id: string;
  scheduleId: string;
  scheduleName: string;
  sessionId?: string;
  status: ExecutionStatus;
  scheduledAt: string;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  retryCount: number;
}

/**
 * Request model for creating a schedule.
 */
export interface CreateScheduleRequest {
  name: string;
  description?: string;
  agentDefinitionId: string;
  scheduleType: ScheduleType;
  cronExpression?: string;
  recurrencePattern?: string;
  scheduledDateTime?: string;
  interval?: string; // ISO 8601 duration format
  timeZone: string;
  isActive: boolean;
  parametersJson?: string;
  maxRetries?: number;
  retryDelay?: string; // ISO 8601 duration format
}

/**
 * Request model for updating a schedule.
 */
export interface UpdateScheduleRequest {
  name: string;
  description?: string;
  agentDefinitionId: string;
  scheduleType: ScheduleType;
  cronExpression?: string;
  recurrencePattern?: string;
  scheduledDateTime?: string;
  interval?: string; // ISO 8601 duration format
  timeZone: string;
  isActive: boolean;
  parametersJson?: string;
  maxRetries?: number;
  retryDelay?: string; // ISO 8601 duration format
}

/**
 * Filters for schedule queries.
 */
export interface ScheduleFilters {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  agentDefinitionId?: string;
  isActive?: boolean;
}

/**
 * Filters for schedule execution queries.
 */
export interface ScheduleExecutionFilters {
  pageNumber?: number;
  pageSize?: number;
  status?: ExecutionStatus;
}
